import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropertyRequestForm from './PropertyRequestForm';

interface Property {
  id: string;
  title: string;
  price: number;
  sale_price?: number;
  rent_price?: number;
  currency?: string;
  location: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  property_type: string;
  status: string;
  available_for_sale?: boolean;
  available_for_rent?: boolean;
  images: string[];
  description: string;
  features: string[];
  year_built?: number;
  parking?: number;
  floor?: number;
  furnished?: boolean;
  city: string;
  address?: string;
  videos?: string[];
  latitude?: number;
  longitude?: number;
  is_featured?: boolean;
  maintenance_fee?: number;
  has_maintenance_fee?: boolean;
  included_services?: string[];
}

interface PropertyDetailsProps {
  property: Property;
  onClose: () => void;
  onBookVisit: (property: Property) => void;
}

export default function PropertyDetails({ property, onClose, onBookVisit }: PropertyDetailsProps) {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [showVideos, setShowVideos] = useState(false);
  const [autoSlide, setAutoSlide] = useState(true);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestType, setRequestType] = useState<'compra' | 'renta'>('compra');

  // Auto-slide para imágenes
  useEffect(() => {
    if (!autoSlide || showVideos || property.images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => 
        prev === property.images.length - 1 ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [autoSlide, showVideos, property.images.length]);

  const formatPrice = (price: number, currency: string = 'MXN') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    } else {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    }
  };

  const formatPropertyPrices = (property: Property) => {
    const currency = property.currency || 'MXN';
    
    if (property.available_for_sale && property.available_for_rent) {
      // Ambos precios disponibles
      return (
        <div className="space-y-3">
          <div className="text-3xl font-bold text-green-600">
            <i className="ri-price-tag-3-line mr-2"></i>
            Venta: {formatPrice(property.sale_price || 0, currency)}
          </div>
          <div className="text-2xl font-bold text-blue-600">
            <i className="ri-calendar-line mr-2"></i>
            Renta: {formatPrice(property.rent_price || 0, currency)}/mes
          </div>
        </div>
      );
    } else if (property.available_for_sale) {
      // Solo venta
      return (
        <div className="text-4xl font-bold text-green-600">
          <i className="ri-price-tag-3-line mr-2"></i>
          {formatPrice(property.sale_price || property.price || 0, currency)}
        </div>
      );
    } else if (property.available_for_rent) {
      // Solo renta
      return (
        <div className="text-4xl font-bold text-blue-600">
          <i className="ri-calendar-line mr-2"></i>
          {formatPrice(property.rent_price || property.price || 0, currency)}/mes
        </div>
      );
    } else {
      // Precio legacy
      return (
        <div className="text-4xl font-bold text-blue-600">
          {formatPrice(property.price, currency)}
        </div>
      );
    }
  };

  const getAvailabilityInfo = (property: Property) => {
    const availabilityTypes = [];
    
    if (property.available_for_sale) {
      availabilityTypes.push({
        type: 'Venta',
        color: 'bg-green-100 text-green-800',
        icon: 'ri-price-tag-3-line'
      });
    }
    
    if (property.available_for_rent) {
      availabilityTypes.push({
        type: 'Renta',
        color: 'bg-blue-100 text-blue-800',
        icon: 'ri-calendar-line'
      });
    }
    
    if (availabilityTypes.length === 0) {
      // Fallback para propiedades legacy
      const legacyType = property.status === 'rented' ? 'Renta' : 'Venta';
      const legacyColor = property.status === 'rented' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
      const legacyIcon = property.status === 'rented' ? 'ri-calendar-line' : 'ri-price-tag-3-line';
      
      availabilityTypes.push({
        type: legacyType,
        color: legacyColor,
        icon: legacyIcon
      });
    }
    
    return availabilityTypes;
  };

  const getServiceDetails = (services: string[]) => {
    const serviceMap: { [key: string]: { icon: string; label: string; color: string; description: string } } = {
      'internet': { 
        icon: 'ri-wifi-line', 
        label: 'Internet', 
        color: 'text-blue-600',
        description: 'Servicio de internet incluido en la renta'
      },
      'mantenimiento': { 
        icon: 'ri-tools-line', 
        label: 'Cuota de Mantenimiento', 
        color: 'text-purple-600',
        description: 'Cuota de mantenimiento incluida'
      },
      'agua': { 
        icon: 'ri-drop-line', 
        label: 'Agua', 
        color: 'text-cyan-600',
        description: 'Servicio de agua incluido en la renta'
      },
      'gas': { 
        icon: 'ri-fire-line', 
        label: 'Gas', 
        color: 'text-orange-600',
        description: 'Servicio de gas incluido en la renta'
      }
    };
    
    return services.map(service => serviceMap[service]).filter(Boolean);
  };

  const getPropertyTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'casa': 'Casa',
      'departamento': 'Departamento',
      'oficina': 'Oficina',
      'local_comercial': 'Local Comercial',
      'terreno': 'Terreno',
      'apartamento': 'Apartamento'
    };
    return typeMap[type] || type;
  };

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'available': t('forSale'),
      'sold': 'Vendida',
      'rented': t('forRent'),
      'pending': 'Pendiente'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'available': 'bg-green-100 text-green-800',
      'sold': 'bg-red-100 text-red-800',
      'rented': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  // Función para mostrar valor o N/A
  const displayValue = (value: any, suffix: string = '') => {
    if (value === null || value === undefined || value === '' || value === 0) {
      return <span className="text-gray-400 italic">N/A</span>;
    }
    return <span className="font-medium">{value}{suffix}</span>;
  };

  // Función para mostrar valor booleano
  const displayBoolean = (value: boolean | undefined) => {
    if (value === undefined || value === null) {
      return <span className="text-gray-400 italic">N/A</span>;
    }
    return <span className="font-medium">{value ? 'Sí' : 'No'}</span>;
  };

  const nextImage = () => {
    setCurrentImageIndex(prev => 
      prev === property.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex(prev => 
      prev === 0 ? property.images.length - 1 : prev - 1
    );
  };

  const nextVideo = () => {
    setCurrentVideoIndex(prev => 
      prev === (property.videos?.length || 0) - 1 ? 0 : prev + 1
    );
  };

  const prevVideo = () => {
    setCurrentVideoIndex(prev => 
      prev === 0 ? (property.videos?.length || 0) - 1 : prev - 1
    );
  };

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setAutoSlide(false);
  };

  const handleRequestClick = (type: 'compra' | 'renta') => {
    setRequestType(type);
    setShowRequestForm(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-800">{property.title}</h2>
            
            {/* Badges de disponibilidad CORREGIDOS */}
            <div className="flex gap-2">
              {getAvailabilityInfo(property).map((availability, index) => (
                <span 
                  key={index}
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${availability.color}`}
                >
                  <i className={`${availability.icon} mr-1`}></i>
                  {availability.type}
                </span>
              ))}
            </div>
            
            {property.is_featured && (
              <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-semibold">
                <i className="ri-star-line mr-1"></i>
                Destacada
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-auto max-h-[calc(95vh-120px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
            {/* Media Section */}
            <div className="space-y-4">
              {/* Media Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowVideos(false)}
                  className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    !showVideos 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className="ri-image-line mr-2"></i>
                  Fotos ({property.images.length})
                </button>
                {property.videos && property.videos.length > 0 && (
                  <button
                    onClick={() => setShowVideos(true)}
                    className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                      showVideos 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-video-line mr-2"></i>
                    Videos ({property.videos.length})
                  </button>
                )}
              </div>

              {/* Main Media Display */}
              <div className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden">
                {!showVideos ? (
                  // Images
                  property.images.length > 0 ? (
                    <>
                      <img
                        src={property.images[currentImageIndex]}
                        alt={`${property.title} - Imagen ${currentImageIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Navigation Arrows */}
                      {property.images.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all cursor-pointer"
                          >
                            <i className="ri-arrow-left-line text-xl"></i>
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all cursor-pointer"
                          >
                            <i className="ri-arrow-right-line text-xl"></i>
                          </button>
                        </>
                      )}

                      {/* Image Counter */}
                      <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} / {property.images.length}
                      </div>

                      {/* Auto-slide Toggle */}
                      {property.images.length > 1 && (
                        <button
                          onClick={() => setAutoSlide(!autoSlide)}
                          className="absolute top-4 left-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all cursor-pointer"
                          title={autoSlide ? 'Pausar auto-deslizamiento' : 'Activar auto-deslizamiento'}
                        >
                          <i className={`ri-${autoSlide ? 'pause' : 'play'}-line`}></i>
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-4xl text-gray-400"></i>
                    </div>
                  )
                ) : (
                  // Videos
                  property.videos && property.videos.length > 0 ? (
                    <>
                      <video
                        src={property.videos[currentVideoIndex]}
                        controls
                        className="w-full h-full object-cover"
                        poster={property.images[0] || undefined}
                      >
                        Tu navegador no soporta la reproducción de videos.
                      </video>

                      {/* Video Navigation */}
                      {property.videos.length > 1 && (
                        <>
                          <button
                            onClick={prevVideo}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all cursor-pointer"
                          >
                            <i className="ri-arrow-left-line text-xl"></i>
                          </button>
                          <button
                            onClick={nextVideo}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all cursor-pointer"
                          >
                            <i className="ri-arrow-right-line text-xl"></i>
                          </button>
                        </>
                      )}

                      {/* Video Counter */}
                      <div className="absolute top-4 right-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                        {currentVideoIndex + 1} / {property.videos.length}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-video-line text-4xl text-gray-400"></i>
                    </div>
                  )
                )}
              </div>

              {/* Thumbnails */}
              {!showVideos && property.images.length > 1 && (
                <div className="grid grid-cols-6 gap-2 max-h-24 overflow-y-auto">
                  {property.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => handleImageClick(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        index === currentImageIndex 
                          ? 'border-blue-600' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Property Information */}
            <div className="space-y-6">
              {/* Price and Type */}
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="mb-4">
                    {formatPropertyPrices(property)}
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="text-lg text-gray-600">
                      {getPropertyTypeLabel(property.property_type)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Información de precios detallada */}
              {property.available_for_sale && property.available_for_rent && (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <i className="ri-information-line mr-2 text-blue-600"></i>
                    Opciones de Adquisición
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-3 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-price-tag-3-line text-green-600"></i>
                        <span className="font-medium text-green-800">Compra</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {formatPrice(property.sale_price || 0, property.currency)}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Precio total de venta</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-calendar-line text-blue-600"></i>
                        <span className="font-medium text-blue-800">Renta</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        {formatPrice(property.rent_price || 0, property.currency)}/mes
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Precio mensual de renta</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Información adicional de renta */}
              {property.available_for_rent && (
                <div className="space-y-4">
                  {/* Cuota de mantenimiento */}
                  {property.has_maintenance_fee && property.maintenance_fee && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-tools-line text-blue-600 text-xl"></i>
                        <span className="font-semibold text-blue-800">Cuota de Mantenimiento</span>
                      </div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatPrice(property.maintenance_fee, property.currency)}/mes
                      </div>
                      <p className="text-sm text-blue-600 mt-1">
                        Cuota mensual de mantenimiento del inmueble
                      </p>
                    </div>
                  )}

                  {/* Servicios incluidos */}
                  {property.included_services && property.included_services.length > 0 && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-checkbox-multiple-line text-green-600 text-xl"></i>
                        <span className="font-semibold text-green-800">Servicios Incluidos en la Renta</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {getServiceDetails(property.included_services).map((service, index) => (
                          <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-green-200">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <i className={`${service.icon} text-lg ${service.color}`}></i>
                            </div>
                            <div>
                              <div className="font-medium text-green-800">{service.label}</div>
                              <div className="text-sm text-green-600">{service.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Location con Google Maps */}
              <div className="flex items-start text-gray-600">
                <i className="ri-map-pin-line mr-2 text-xl mt-1"></i>
                <div className="flex-1">
                  <div className="font-medium">{property.location}</div>
                  {property.address && (
                    <div className="text-sm text-gray-500">{property.address}</div>
                  )}
                  {property.city && property.city !== property.location && (
                    <div className="text-sm text-gray-500">{property.city}</div>
                  )}
                  
                  {/* Google Maps - Reemplaza latitud/longitud */}
                  {property.latitude && property.longitude && (
                    <div className="mt-3">
                      <div className="bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                        <iframe
                          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${property.latitude},${property.longitude}&zoom=15`}
                          width="100%"
                          height="200"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Ubicación de la propiedad"
                        ></iframe>
                      </div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-800 text-sm cursor-pointer"
                      >
                        <i className="ri-map-2-line mr-1"></i>
                        Abrir en Google Maps
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Key Features - OCULTAR HABITACIONES SI ES 0 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {property.bedrooms > 0 && (
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <i className="ri-hotel-bed-line text-2xl text-blue-600 mb-2"></i>
                    <div className="font-semibold">{property.bedrooms}</div>
                    <div className="text-sm text-gray-600">{t('bedrooms')}</div>
                  </div>
                )}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-drop-line text-2xl text-blue-600 mb-2"></i>
                  <div className="font-semibold">{property.bathrooms}</div>
                  <div className="text-sm text-gray-600">{t('bathrooms')}</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <i className="ri-square-line text-2xl text-blue-600 mb-2"></i>
                  <div className="font-semibold">{property.area}</div>
                  <div className="text-sm text-gray-600">m²</div>
                </div>
                {property.parking && property.parking > 0 && (
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <i className="ri-car-line text-2xl text-blue-600 mb-2"></i>
                    <div className="font-semibold">{property.parking}</div>
                    <div className="text-sm text-gray-600">Parking</div>
                  </div>
                )}
              </div>

              {/* Información Detallada de la Propiedad - OCULTAR HABITACIONES SI ES 0 */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-information-line mr-2 text-blue-600"></i>
                  Información Detallada
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Tipo de propiedad:</span>
                    {displayValue(getPropertyTypeLabel(property.property_type))}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Estado:</span>
                    {displayValue(getStatusLabel(property.status))}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Área total:</span>
                    {displayValue(property.area, ' m²')}
                  </div>
                  {property.bedrooms > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Habitaciones:</span>
                      {displayValue(property.bedrooms)}
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Baños:</span>
                    {displayValue(property.bathrooms)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Estacionamientos:</span>
                    {displayValue(property.parking)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Piso:</span>
                    {displayValue(property.floor)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Año de construcción:</span>
                    {displayValue(property.year_built)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Amueblado:</span>
                    {displayBoolean(property.furnished)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Moneda:</span>
                    {displayValue(property.currency)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Propiedad destacada:</span>
                    {displayBoolean(property.is_featured)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Disponible para venta:</span>
                    {displayBoolean(property.available_for_sale)}
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Disponible para renta:</span>
                    {displayBoolean(property.available_for_rent)}
                  </div>
                </div>
              </div>

              {/* Description */}
              {property.description && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Descripción</h3>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {property.description}
                  </p>
                </div>
              )}

              {/* Features */}
              {property.features && property.features.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Características</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <i className="ri-check-line text-green-500"></i>
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => onBookVisit(property)}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  {t('bookVisit')}
                </button>
                
                {/* Botones específicos según disponibilidad */}
                {property.available_for_sale && (
                  <button 
                    onClick={() => handleRequestClick('compra')}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Comprar
                  </button>
                )}
                
                {property.available_for_rent && (
                  <button 
                    onClick={() => handleRequestClick('renta')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-key-line mr-2"></i>
                    Rentar
                  </button>
                )}
                
                <button className="px-6 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <i className="ri-heart-line text-gray-600"></i>
                </button>
                <button className="px-6 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <i className="ri-share-line text-gray-600"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Property Request Form Modal */}
      {showRequestForm && (
        <PropertyRequestForm
          property={property}
          requestType={requestType}
          onClose={() => setShowRequestForm(false)}
        />
      )}
    </div>
  );
}
